import React, { useState, useEffect, useMemo } from 'react';
import { CompanyI, FilterConditionType, FilterStatus, ImportExportFiler, SortCondition, SortConditionType, VehicleCategoryI } from '.';
import { compareValue } from 'utils/commonFunctions';
import * as S from './styles';
import { CONDITION_ALL, DEFAULT_FILTER_SORT } from 'constants/misc';
import loginStorage from 'utils/loginStorage';

type DetailFilterAndSortProps = {
  openModal?: boolean
  tenantId: string
  companyList: CompanyI[]
  vehicleCategories: VehicleCategoryI[]
  filterCondition: FilterConditionType;
  sortCondition: SortConditionType;
  getCompanyList: () => void
  updateFilterSearchBooking: (filter: { filter?: FilterConditionType, sort?: SortConditionType }) => void
  oneResetFilter(): void
};

const DetailFilterAndSort: React.FC<DetailFilterAndSortProps> = ({
  openModal,
  tenantId,
  filterCondition = {},
  sortCondition = {},
  companyList,
  vehicleCategories,
  getCompanyList,
  updateFilterSearchBooking,
  oneResetFilter
}) => {
  const [filter, setFilter] = useState(filterCondition);
  const [sort, setSort] = useState(sortCondition);

  useEffect(() => {
    getCompanyList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!openModal) {
      setFilter(filterCondition);
      setSort(sortCondition);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal, filterCondition]);

  const isSameWithDefaultFilterSort = useMemo(() => {
    const defaultFilter = DEFAULT_FILTER_SORT.filter as FilterConditionType;

    return (
      compareValue(filter.status, defaultFilter.status) &&
      compareValue(filter.startDate, defaultFilter.startDate) &&
      compareValue(filter.endDate, defaultFilter.endDate) &&
      compareValue(filter.operationType, defaultFilter.operationType) &&
      compareValue(filter.vehicleControllerCompanyId, defaultFilter.vehicleControllerCompanyId) &&
      compareValue(filter.driverCompanyId, defaultFilter.driverCompanyId) &&
      filter.vehicleCategoryIds?.includes(CONDITION_ALL) &&
      compareValue(sort, DEFAULT_FILTER_SORT.sort)

    );
  }, [filter.driverCompanyId, filter.endDate, filter.operationType, filter.startDate, filter.status, filter.vehicleCategoryIds, filter.vehicleControllerCompanyId, sort]);

  const allVehicleCategoryIds = useMemo(() => {
    return vehicleCategories
      ?.filter((it) => it.display_flag)
      ?.map((it) => String(it.parameter_id)) || [];
  }, [vehicleCategories]);

  const handleClearFilterAndSort = () => {
    const clearedFilter = {
      ...DEFAULT_FILTER_SORT.filter as FilterConditionType,
      vehicleCategoryIds: [...allVehicleCategoryIds, CONDITION_ALL],
    };

    updateFilterSearchBooking({ filter: clearedFilter, sort: DEFAULT_FILTER_SORT.sort as SortConditionType });

    loginStorage.setFilterSortBookingNormal(tenantId, {
      filter: DEFAULT_FILTER_SORT.filter as FilterConditionType,
      sort: DEFAULT_FILTER_SORT.sort as SortConditionType,
    });

    oneResetFilter();
  };

  const renderCategories = () => {
    const categoriesFormat = vehicleCategories?.filter(category => category.display_flag) || [];
    const IS_SELECT_ALL = filter?.vehicleCategoryIds?.includes(CONDITION_ALL);
    const selectedCategories = IS_SELECT_ALL ? categoriesFormat : categoriesFormat.filter(category =>
      filter?.vehicleCategoryIds?.includes(category.parameter_id.toString())
    );

    return selectedCategories?.map((category, idx) => (
      <span key={category.vehicle_category_id}>
        {category.parameter_name}
        {idx < selectedCategories?.length - 1 && '、'}
      </span>
    ));
  };

  const renderFilterStatus = () => {
    const statusCondition = FilterStatus?.find(condition => compareValue(condition.value, filter?.status));
    return statusCondition ? <span>{statusCondition.label}</span> : null;
  };

  const renderOperationType = () => {
    const operationCondition = ImportExportFiler?.find(condition => compareValue(condition.value, filter?.operationType));
    return operationCondition ? <span>{operationCondition.label}</span> : null;
  };

  const renderCompanyName = (companyId: string | number) => {
    const company = companyList?.find(company => compareValue(company.company_id, companyId));
    return company ? <span>{company.company_name}</span> : null;
  };

  const renderSortCondition = () => {
    const sortConditionItem = SortCondition?.find(condition => compareValue(condition.value, sort));
    return sortConditionItem ? (
      <span>{sortConditionItem.label}</span>
    ) : null;
  };

  if (isSameWithDefaultFilterSort) return null;

  return (
    <S.WrapperDetailFilter className="custom-scrollbar">
      <div>
        <div className='flex justify-center items-center'>
          <S.StyledButton onClick={handleClearFilterAndSort}>リセット</S.StyledButton>
        </div>
        <div>
          <S.StyledStrong>フィルター:</S.StyledStrong>
          <S.StyledUl>
            {filter.status && (
              <S.StyledLi>
                <S.StyleTitle>ステータス：</S.StyleTitle>
                {renderFilterStatus()}
              </S.StyledLi>
            )}
            {(filter.startDate || filter.endDate) && (
              <S.StyledLi>
                <S.StyleTitle>受付日・予約開始日：</S.StyleTitle>
                <br />
                <span>
                  {filter.startDate || '開始日'} ~ {filter.endDate || '開始日'}
                </span>
              </S.StyledLi>
            )}
            {filter.operationType && (
              <S.StyledLi>
                <S.StyleTitle>積み卸し：</S.StyleTitle>
                {renderOperationType()}
              </S.StyledLi>
            )}
            {filter.vehicleControllerCompanyId && (
              <S.StyledLi>
                <S.StyleTitle>配車担当会社名: </S.StyleTitle>
                {renderCompanyName(filter.vehicleControllerCompanyId)}
              </S.StyledLi>
            )}
            {filter.driverCompanyId && (
              <S.StyledLi>
                <S.StyleTitle>運送会社名: </S.StyleTitle>
                {renderCompanyName(filter.driverCompanyId)}
              </S.StyledLi>
            )}
            {filter.vehicleCategoryIds && filter.vehicleCategoryIds.length > 0 && (
              <S.StyledLi>
                <S.StyleTitle>車両形態: </S.StyleTitle>
                {renderCategories()}
              </S.StyledLi>
            )}
          </S.StyledUl>
        </div>
        <div>
          <S.StyledStrong>ソート:</S.StyledStrong>
          <S.StyledUl>
            <S.StyledLi>
              {renderSortCondition()}
            </S.StyledLi>
          </S.StyledUl>
        </div>
      </div>
    </S.WrapperDetailFilter>
  );
};

export default DetailFilterAndSort;
